import { Form } from 'antd';
import moment from 'moment';
import { useCallback, useMemo, useRef, useState } from 'react';

import {
  useDeleteWorkBreaksMutation,
  useLazyCheckEmployeeWorkBreakQuery,
  useUpdateWorkBreakMutation,
} from '@app/api/WorkSchedule';
import { useUserPrint } from '@app/hooks';
import { useConfirmNew } from '@app/providers/NewConfirmProvider/useConfirmNew';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { isValidTime } from '@app/shared/components/TimeInput';

import { MIN_BREAK_DURATION_MINUTES, isCompleteTimeRange } from '../utils';

const { useForm, useWatch } = Form;
type BreakActionMode = 'create' | 'edit';

// Время в формате ЧЧ:ММ, вводится вручную (us/11381).
interface BreakActionFormValues {
  timeS: string;
  timeE: string;
}

interface UseEmployeeBreakActionModalParams {
  breakId?: number;
  date: string;
  employeeId: number;
  initialTimeS: string;
  initialTimeE: string;
  isOpen: boolean;
  mode: BreakActionMode;
  onClose(): void;
  onSuccess(): void;
}

export const useEmployeeBreakActionModal = ({
  breakId,
  date,
  employeeId,
  initialTimeS,
  initialTimeE,
  isOpen,
  mode,
  onClose,
  onSuccess,
}: UseEmployeeBreakActionModalParams) => {
  const [form] = useForm<BreakActionFormValues>();
  const [warningText, setWarningText] = useState('');
  const initializedModalKeyRef = useRef<string | null>(null);

  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();
  const { show: showConfirm } = useConfirmNew();

  const [updateWorkBreak, { isLoading: isUpdateLoading }] =
    useUpdateWorkBreakMutation();
  const [deleteWorkBreaks, { isLoading: isDeleteLoading }] =
    useDeleteWorkBreaksMutation();
  const [checkEmployeeWorkBreak] = useLazyCheckEmployeeWorkBreakQuery();

  const selectedDateString = useMemo(
    () => moment(date, 'YYYY-MM-DD').format('YYYY-MM-DD'),
    [date]
  );

  const selectedDateLabel = useMemo(
    () => moment(selectedDateString, 'YYYY-MM-DD').format('DD.MM.YYYY'),
    [selectedDateString]
  );

  const currentBreakLabel = useMemo(() => {
    if (!isValidTime(initialTimeS) || !isValidTime(initialTimeE)) {
      return 'Не удалось определить перерыв';
    }

    return `${initialTimeS} - ${initialTimeE}`;
  }, [initialTimeE, initialTimeS]);

  const normalizedBreakId = breakId ?? 0;

  const isEditMode = mode === 'edit';

  const isLoading = isUpdateLoading || isDeleteLoading;

  const successMessage = useMemo(
    () =>
      isEditMode
        ? 'Перерыв в расписании обновлен'
        : 'Перерыв в расписании добавлен',
    [isEditMode]
  );

  const modalInitialStateKey = useMemo(
    () =>
      [
        mode,
        normalizedBreakId,
        employeeId,
        selectedDateString,
        initialTimeS,
        initialTimeE,
      ].join(':'),
    [
      mode,
      normalizedBreakId,
      employeeId,
      selectedDateString,
      initialTimeS,
      initialTimeE,
    ]
  );

  const watchedTimeS = useWatch('timeS', form);
  const watchedTimeE = useWatch('timeE', form);

  const buildIsoString = useCallback(
    (time?: string) =>
      isValidTime(time) ? `${selectedDateString}T${time}:00.000Z` : null,
    [selectedDateString]
  );

  const checkBreakConflicts = useCallback(
    async (timeS?: string, timeE?: string) => {
      if (!ActiveBranchId || timeS == null || timeE == null) return;

      const timeSValue = buildIsoString(timeS);
      const timeEValue = buildIsoString(timeE);

      if (!timeSValue || !timeEValue) return;

      try {
        const result = await checkEmployeeWorkBreak({
          ActiveBranchId,
          employeeId,
          timeS: timeSValue,
          timeE: timeEValue,
        }).unwrap();

        setWarningText(result.WarningForEdit ?? '');
      } catch (error) {
        showNotification(error);
      }
    },
    [
      buildIsoString,
      checkEmployeeWorkBreak,
      employeeId,
      ActiveBranchId,
      showNotification,
    ]
  );

  const syncFormAndCheckBreakConflicts = useCallback(() => {
    if (!isOpen) {
      initializedModalKeyRef.current = null;
      return;
    }

    const isNewModalSession =
      initializedModalKeyRef.current !== modalInitialStateKey;

    if (isNewModalSession) {
      initializedModalKeyRef.current = modalInitialStateKey;

      form.setFieldsValue({
        timeS: initialTimeS,
        timeE: initialTimeE,
      });

      setWarningText('');
      return;
    }

    // Пока время вводится или интервал некорректен, бэкенд не спрашиваем.
    if (
      !isCompleteTimeRange(
        watchedTimeS,
        watchedTimeE,
        MIN_BREAK_DURATION_MINUTES
      )
    ) {
      setWarningText('');
      return;
    }

    const isInitialInterval =
      watchedTimeS === initialTimeS && watchedTimeE === initialTimeE;

    if (isInitialInterval) {
      setWarningText('');
      return;
    }

    void checkBreakConflicts(watchedTimeS, watchedTimeE);
  }, [
    form,
    isOpen,
    initialTimeS,
    initialTimeE,
    watchedTimeS,
    watchedTimeE,
    modalInitialStateKey,
    checkBreakConflicts,
  ]);

  const handleClose = useCallback(() => {
    form.resetFields();
    setWarningText('');
    onClose();
  }, [form, onClose]);

  const handleFinish = useCallback(
    async (values: BreakActionFormValues) => {
      if (!ActiveBranchId) return;
      if (isEditMode && !breakId) return;

      const { timeS, timeE } = values;

      const timeSValue = buildIsoString(timeS);
      const timeEValue = buildIsoString(timeE);

      if (!timeSValue || !timeEValue) return;

      try {
        await updateWorkBreak({
          ActiveBranchId,
          data: {
            Id: isEditMode ? normalizedBreakId : 0,
            UserId: employeeId,
            BranchId: ActiveBranchId,
            TimeS: timeSValue,
            TimeE: timeEValue,
          },
        }).unwrap();

        showNotification(successMessage, 'success');
        onSuccess();
        handleClose();
      } catch (error) {
        showNotification(error);
      }
    },
    [
      breakId,
      buildIsoString,
      employeeId,
      handleClose,
      isEditMode,
      normalizedBreakId,
      ActiveBranchId,
      onSuccess,
      showNotification,
      successMessage,
      updateWorkBreak,
    ]
  );

  const deleteBreak = useCallback(async () => {
    if (!isEditMode || !ActiveBranchId || !breakId) return;

    try {
      await deleteWorkBreaks({
        ActiveBranchId,
        data: [breakId],
      }).unwrap();

      showNotification('Перерыв в расписании удален', 'success');
      onSuccess();
      handleClose();
    } catch (error) {
      showNotification(error);
    }
  }, [
    breakId,
    deleteWorkBreaks,
    handleClose,
    isEditMode,
    ActiveBranchId,
    onSuccess,
    showNotification,
  ]);

  const handleDelete = useCallback(() => {
    if (!isEditMode || !ActiveBranchId || !breakId) return;

    showConfirm({
      title: 'Удалить перерыв?',
      message: `Перерыв ${currentBreakLabel} будет удален из расписания.`,
      confirmText: 'Удалить',
      cancelText: 'Отмена',
      dataTestId: 'delete-break',
      onConfirm: () => void deleteBreak(),
    });
  }, [
    ActiveBranchId,
    breakId,
    currentBreakLabel,
    deleteBreak,
    isEditMode,
    showConfirm,
  ]);

  const handleConfirm = useCallback(() => {
    form.submit();
  }, [form]);

  return {
    form,
    warningText,
    currentBreakLabel,
    selectedDateLabel,
    isLoading,
    handleClose,
    handleConfirm,
    handleDelete,
    handleFinish,
    syncFormAndCheckBreakConflicts,
  };
};
